import { useEffect } from 'react';
import {
  buildCountryOptionKey,
  countryToPhoneFields,
  findCountryByEnName,
  findCountryByOptionKey,
  formatCountryOptionLabel,
} from '@formosa/shared/utils/countryUtils';
import { useCountryOptions } from '../../hooks/useCountryOptions';

/**
 * 手機登入／註冊用的國家下拉 + 冠碼（冠碼隨國家自動帶入）。
 */
export default function AuthPhoneCountryFields({
  variant = 'form',
  countryKey = '',
  countryEnName = '',
  phoneAreaCode = '',
  onChange,
  allowedEnNames,
  disabled = false,
  idPrefix = 'auth-phone',
}) {
  const { countries, loading, defaultCountry } = useCountryOptions(allowedEnNames);

  useEffect(() => {
    if (loading || countries.length === 0 || !onChange) {
      return;
    }

    if (countryKey) {
      const selected = findCountryByOptionKey(countries, countryKey);
      if (selected) {
        return;
      }
    }

    if (countryEnName) {
      const legacy = findCountryByEnName(countries, countryEnName, phoneAreaCode)
        || findCountryByEnName(countries, countryEnName);
      if (legacy) {
        const next = countryToPhoneFields(legacy);
        if (next.countryKey !== countryKey || next.phoneAreaCode !== phoneAreaCode) {
          onChange(next);
        }
        return;
      }
    }

    if (!countryKey && !countryEnName && defaultCountry) {
      onChange(countryToPhoneFields(defaultCountry));
    }
  }, [
    loading,
    countries,
    countryKey,
    countryEnName,
    phoneAreaCode,
    defaultCountry,
    onChange,
  ]);

  function handleCountrySelect(event) {
    const selected = findCountryByOptionKey(countries, event.target.value);
    if (!selected) {
      onChange?.({ countryKey: '', countryEnName: '', phoneAreaCode: '' });
      return;
    }
    onChange?.(countryToPhoneFields(selected));
  }

  const selectValue =
    countryKey
    || buildCountryOptionKey(findCountryByEnName(countries, countryEnName, phoneAreaCode))
    || '';

  const countrySelect = (
    <select
      id={`${idPrefix}-country`}
      className={variant === 'login' ? 'auth-select' : undefined}
      value={selectValue}
      onChange={handleCountrySelect}
      disabled={disabled || loading || countries.length === 0}
      required
    >
      <option value="">{loading ? '載入國家列表…' : '請選擇國家/地區'}</option>
      {countries.map((item) => (
        <option key={buildCountryOptionKey(item)} value={buildCountryOptionKey(item)}>
          {formatCountryOptionLabel(item)}
        </option>
      ))}
    </select>
  );

  const areaCodeField = (
    <input
      id={`${idPrefix}-area-code`}
      type="text"
      className={variant === 'login' ? 'auth-input auth-input--plain' : undefined}
      value={phoneAreaCode}
      readOnly
      tabIndex={-1}
      aria-label="冠碼"
      placeholder="冠碼"
    />
  );

  if (variant === 'login') {
    return (
      <div className="auth-form-row auth-form-row--compact">
        <label className="auth-input-wrap auth-input-wrap--grow">
          {countrySelect}
        </label>
        <label className="auth-input-wrap auth-input-wrap--area-code">
          {areaCodeField}
        </label>
      </div>
    );
  }

  return (
    <div className="form-row">
      <div className="form-field form-field--grow">
        <label htmlFor={`${idPrefix}-country`}>國家/地區</label>
        {countrySelect}
      </div>
      <div className="form-field form-field--area-code">
        <label htmlFor={`${idPrefix}-area-code`}>冠碼</label>
        {areaCodeField}
      </div>
    </div>
  );
}
